import { useState, useEffect, useCallback, useRef } from 'react';

import { useApi } from '@/context/api-context';
import { useVisualization } from '@/context/visualization-context';
import type { LoadProgressPayload } from '@/visualization';
import { WindowLevelService } from '@/visualization/services/window-level-service';
import { ViewerSessionOrchestrator } from '@/visualization/viewer-session-orchestrator';
import { getWorkflowConfig } from '@/workflows';
import type { WorkflowConfig } from '@/workflows/types';

// ============================================================================
// Types
// ============================================================================

type ViewerSessionStatus = 'initializing' | 'loading' | 'ready' | 'error';

export interface ViewerSessionState {
    status: ViewerSessionStatus;
    config: WorkflowConfig | null;
    progress: LoadProgressPayload;
    error: Error | null;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Unified hook that replaces both `useCase()` (in ViewerPage) and
 * `useWorkflowViewer()` (in ViewerContent).
 *
 * Phases:
 *   1. `initializing` — fetch case data, resolve workflow, set metadata
 *   2. `loading` — subscribe to progress events, load visualization data
 *   3. `ready` — data loaded, transforms complete
 *   4. `error` — any failure during phases 1-2
 */
export function useViewerSession(caseId: string): ViewerSessionState {
    const api = useApi();
    const { getWorkflowService, setActiveWorkflowService, getServiceManager } = useVisualization();

    const [status, setStatus] = useState<ViewerSessionStatus>('initializing');
    const [config, setConfig] = useState<WorkflowConfig | null>(null);
    const [progress, setProgress] = useState<LoadProgressPayload>({
        loaded: 0,
        total: 0,
        isComplete: false,
    });
    const [error, setError] = useState<Error | null>(null);

    // Refs for cleanup
    const unsubscribersRef = useRef<Array<() => void>>([]);

    // Stable refs to avoid retriggering the effect on HMR reloads
    const getWorkflowServiceRef = useRef(getWorkflowService);
    getWorkflowServiceRef.current = getWorkflowService;

    const setActiveWorkflowServiceRef = useRef(setActiveWorkflowService);
    setActiveWorkflowServiceRef.current = setActiveWorkflowService;

    const getServiceManagerRef = useRef(getServiceManager);
    getServiceManagerRef.current = getServiceManager;

    const apiRef = useRef(api);
    apiRef.current = api;

    const cleanup = useCallback(() => {
        unsubscribersRef.current.forEach((unsub) => unsub());
        unsubscribersRef.current = [];
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function run() {
            try {
                // Phase 1: Fetch case + resolve workflow → 'initializing'
                setStatus('initializing');
                setError(null);

                const orchestrator = new ViewerSessionOrchestrator(
                    apiRef.current.cases,
                    getWorkflowServiceRef.current
                );
                const result = await orchestrator.initialize(caseId, controller.signal);

                // Bridge to React: look up WorkflowConfig (contains ComponentTypes)
                const workflowConfig = getWorkflowConfig(result.workflowId);
                if (!workflowConfig) {
                    throw new Error(`Unsupported workflow: "${result.workflowId}"`);
                }

                setActiveWorkflowServiceRef.current(result.workflowService);
                setConfig(workflowConfig);

                // Apply the workflow's default W/L preset on load (and make it the
                // target of "Reset to defaults"). Falls back to 800/200 when unset.
                const wlService = getServiceManagerRef
                    .current()
                    .getService<WindowLevelService>(WindowLevelService.SERVICE_NAME);
                wlService.configureDefaultPreset(workflowConfig.defaultWindowLevelPresetId ?? null);
                wlService.resetToDefaults();

                // HMR guard: if the service already loaded data, skip straight to ready
                if (result.workflowService.isReady()) {
                    setStatus('ready');
                    return;
                }

                // Phase 2: Subscribe to progress events, start loading → 'loading'
                setStatus('loading');

                unsubscribersRef.current.push(
                    result.workflowService.subscribe<LoadProgressPayload>(
                        result.workflowService.EVENTS.LOAD_PROGRESS,
                        (progressPayload) => {
                            setProgress(progressPayload);
                        }
                    )
                );

                unsubscribersRef.current.push(
                    result.workflowService.subscribe<LoadProgressPayload>(
                        result.workflowService.EVENTS.LOAD_COMPLETE,
                        () => {
                            const ready = result.workflowService.isReady();
                            if (ready) {
                                setStatus('ready');
                            } else {
                                setError(
                                    new Error(
                                        'Required visualization data could not be loaded. Please contact support.'
                                    )
                                );
                                setStatus('error');
                            }
                        }
                    )
                );

                unsubscribersRef.current.push(
                    result.workflowService.subscribe<{ message: string; error?: unknown }>(
                        result.workflowService.EVENTS.TRANSFORM_ERROR,
                        ({ message, error: transformError }) => {
                            console.error('Transform error:', message, transformError);
                            setError(new Error(message));
                            setStatus('error');
                        }
                    )
                );

                await result.workflowService.load(caseId, controller.signal);
            } catch (err) {
                // Ignore abort errors — they are expected on cleanup
                if (err instanceof DOMException && err.name === 'AbortError') {
                    return;
                }
                console.error('Error initializing viewer session:', err);
                setError(err instanceof Error ? err : new Error('Failed to load visualization data'));
                setStatus('error');
            }
        }

        run();

        return () => {
            controller.abort();
            cleanup();
        };
    }, [caseId, cleanup]);

    return { status, config, progress, error };
}
